import type { JSX } from "react";
import {
  dot,
  dotActive,
  dotInactive,
  dotMixed,
  dotUnknown,
  toggle,
  wrapper
} from "./ActivationDotCell.css";

interface ActivationDotCellProps {
  /** The activation state to draw. */
  value: unknown;
  /** Overrides the state's own label, for a caller that names states its way. */
  label?: string;
  /** Renders the dot as a button that calls this on click. */
  onClick?: () => void;
  /** Draw nothing for an unknown value instead of the grey dot. */
  hideUnknown?: boolean;
}

/**
 * An activation state as a dot: 1 active (green), 0 inactive (red), a fraction
 * partly active (grey — a group whose members disagree, or a style active in
 * some of its locations), anything else unknown. Serves as a grid
 * `cellRenderer` (which supplies `value`) and as a standalone dot alike.
 */
export function ActivationDotCell({
  value,
  label,
  onClick,
  hideUnknown = false
}: ActivationDotCellProps): JSX.Element | null {
  const state =
    value === 1
      ? { className: dotActive, label: "Active" }
      : value === 0
        ? { className: dotInactive, label: "Inactive" }
        : typeof value === "number" && value > 0 && value < 1
          ? { className: dotMixed, label: "Partly active" }
          : { className: dotUnknown, label: "Unknown" };

  if (hideUnknown && state.className === dotUnknown) return null;

  const dotElement = <span className={`${dot} ${state.className}`} />;

  if (!onClick) {
    return (
      <div className={wrapper} aria-label={label ?? state.label}>
        {dotElement}
      </div>
    );
  }

  return (
    <div className={wrapper}>
      <button
        type="button"
        className={toggle}
        aria-label={label ?? state.label}
        onClick={onClick}
      >
        {dotElement}
      </button>
    </div>
  );
}
